import type { Id } from "@repo/convex/dataModel";
import { buildAssistantThreadPath, parseAssistantThreadIdFromPath } from "@repo/shared";
import * as React from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { trackNavigationEvent } from "@/lib/analytics/domain";
import type { TabMemory } from "@/routing/core/locations";
import { useProjectNavigate, useProjectRoute } from "../routing/project-route-context";
import {
  type ClarificationIndicatorLinkState,
  type ClarificationRequestFilter,
  type ClarificationViewMode,
  clarificationViewModeMap,
  clarificationViewModeMapReverse,
  filterMap,
  filterMapReverse,
  type RightSidebarTab,
  type TabSearchParams,
  tabMap,
  tabMapReverse,
} from "../types";

interface RightSidebarProviderProps {
  children: React.ReactNode;
  projectId: Id<"projects">;
  searchParams: TabSearchParams;
  memory: TabMemory;
  tabId: string;
  updateTabSearchParams: (
    tabId: string,
    params: TabSearchParams,
    options?: { replace?: boolean }
  ) => void;
  updateTabMemory: (tabId: string, updates: Partial<TabMemory>) => void;
}

interface RightSidebarProjectContextValue {
  projectId: Id<"projects">;
  tabId: string;
}

interface RightSidebarNavigationContextValue {
  navigate: (path: string) => void;
  currentPath: string;
  rememberedAssistantThreadId: string | null;
  setRememberedAssistantThread: (threadId: string) => void;
  clearRememberedAssistantThread: () => void;
}

export type RightSidebarClaudiaVisibleItem = {
  entityTypeKey: string;
  entityId: string;
  entityName: string;
  entitySlug?: string;
};

export type RightSidebarClaudiaViewContext =
  | {
      kind: "project";
      label?: string;
    }
  | {
      kind: "entity-tab";
      entityTypeKey: string;
      entityId: string;
      entityName: string;
      tabKey?: string;
      tabLabel?: string;
    }
  | {
      kind: "entity-section";
      entityTypeKey: string;
      entityId: string;
      entityName: string;
      tabKey: string;
      tabLabel: string;
      fieldId: string;
      fieldLabel: string;
      guidance?: string;
      expectedMentionTypes?: string[];
    }
  | {
      kind: "folder";
      label: string;
      routePath: string;
      parentFolderLabel?: string;
      parentFolderRoutePath?: string;
      createTargetLabel?: string;
      activeFilters?: Record<string, unknown> | null;
      visibleItems?: RightSidebarClaudiaVisibleItem[];
      hiddenMatchingItems?: RightSidebarClaudiaVisibleItem[];
      allScopedItems?: RightSidebarClaudiaVisibleItem[];
    }
  | {
      kind: "collection";
      label: string;
      routePath: string;
      parentFolderLabel?: string;
      parentFolderRoutePath?: string;
      createTargetLabel?: string;
      activeFilters?: Record<string, unknown> | null;
      visibleItems?: RightSidebarClaudiaVisibleItem[];
      hiddenMatchingItems?: RightSidebarClaudiaVisibleItem[];
      allScopedItems?: RightSidebarClaudiaVisibleItem[];
    };

export type QualityGuardFindingsReference = {
  entityId: string;
  scope?:
    | { kind: "section"; entityId: string; tabKey: string; fieldId: string }
    | { kind: "tab"; entityId: string; tabKey: string }
    | { kind: "element"; entityId: string };
};

interface RightSidebarClaudiaAskIntent {
  context: RightSidebarClaudiaViewContext;
  trigger: "ask-claudia" | "editor-follow-up";
  sourceRunId?: string;
  initialPrompt: string;
  requestKey: number;
  /**
   * When true, the panel opens a fresh Claudia thread before consuming this intent
   * (the composer prefill/send is gated until that new thread is active — see
   * `claudia-panel.tsx`). Additive and optional: existing callers omit it and keep
   * submitting into the current thread. LAT-620.
   */
  startNewThread?: boolean;
  /**
   * Skills to attach for this turn (deterministic, turn-scoped activation). Forwarded
   * verbatim into `submitMessage` (the backend already accepts `attachedSkillNames`).
   * Does not alter the backend `contextHandoff` validator. LAT-620.
   */
  attachedSkillNames?: string[];
  /**
   * LAT-935: Quality Guard findings this message is about, forwarded verbatim into
   * `submitMessage`. The backend resolves each id, verifies it belongs to the project, and
   * renders the full finding into the prompt — which is what lets `initialPrompt` be one
   * sentence instead of a reference block. When this is empty the consumer falls back to
   * `fallbackPrompt` (see `resolveClaudiaFixPrompt`).
   */
  findingRefs?: {
    findingId: string;
    canonicalKey: string;
    ruleId?: string | null;
    missingAspects: string[];
  }[];
  /**
   * Where Quality Guard 2 is of record: the element, or one of its scopes, whose open findings
   * this message is about. The server expands it to every open finding there.
   */
  findingsReference?: QualityGuardFindingsReference;
  /** Full-payload prompt used when the structured refs above are absent. LAT-935. */
  fallbackPrompt?: string;
  /**
   * LAT-935: submit this intent as soon as the thread is ready instead of leaving a draft in
   * the composer. Panel-only — the full-screen surface drops `startNewThread` intents already.
   */
  autoSubmit?: boolean;
}

interface RightSidebarClaudiaContextValue {
  currentViewContext: RightSidebarClaudiaViewContext | null;
  setClaudiaViewContext: (context: RightSidebarClaudiaViewContext | null) => void;
  pendingAskClaudiaIntent: RightSidebarClaudiaAskIntent | null;
  /**
   * Replaces the pending ask-intent. There is exactly ONE slot: a newer intent replaces an
   * unconsumed older one, it is never queued behind it. Consumers dedup on `requestKey`
   * (see `useAskClaudiaNewThreadIntent`) — the previous name, `queue…`, promised a queue that
   * never existed and invited the LAT-904 race. LAT-904 D6.
   */
  setAskClaudiaIntent: (intent: Omit<RightSidebarClaudiaAskIntent, "requestKey">) => void;
  clearAskClaudiaIntent: () => void;
}

function areClaudiaViewContextsEqual(
  a: RightSidebarClaudiaViewContext | null,
  b: RightSidebarClaudiaViewContext | null
) {
  if (a === b) return true;
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

interface RightSidebarUiContextValue {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
  activePanel: RightSidebarTab;
  setActivePanel: (panel: RightSidebarTab) => void;
  expandToPanel: (panel: RightSidebarTab, options?: { replace?: boolean }) => void;
  togglePanel: (panel: RightSidebarTab) => void;
}

interface RightSidebarVersionsViewState {
  currentViewedVersionId: Id<"entityVersions"> | null;
  diffTargetVersionId: Id<"entityVersions"> | null;
  /**
   * The newest committed version of the open element, published so a sibling panel can offer a
   * comparison against it without subscribing to the version list a second time.
   */
  latestSavedVersionId: Id<"entityVersions"> | null;
  isDiffMode: boolean;
  isRouteVersionMode: boolean;
}

export type VersionCompareRouteBehavior = "saved-diff" | "preserve";

export interface VersionCompareOptions {
  routeBehavior?: VersionCompareRouteBehavior;
}

type RightSidebarVersionAction =
  | { kind: "preview"; versionId: Id<"entityVersions">; versionNumber?: number }
  | { kind: "exitPreview" }
  | {
      kind: "compare";
      versionId: Id<"entityVersions">;
      versionNumber?: number;
      routeBehavior?: VersionCompareRouteBehavior;
    }
  | {
      kind: "comparePair";
      baselineVersionId: Id<"entityVersions"> | null;
      targetVersionId: Id<"entityVersions">;
      baselineVersionNumber?: number;
      targetVersionNumber?: number;
      routeBehavior?: VersionCompareRouteBehavior;
    }
  | { kind: "exitCompare" }
  | { kind: "closePanel" };

type RightSidebarVersionActionIntent = RightSidebarVersionAction & {
  requestId: number;
};

interface RightSidebarEntityContextValue {
  entityType: string | null;
  entityId: string | null;
  entityName: string | null;
  entityTabKey: string | null;
  entityFieldId: string | null;
  /**
   * Which group of the Quality Guard panel the caller wants looked at, when it wants one
   * in particular. The panel scrolls to it once and clears it, so a later open of the
   * same entity lands where the reader left off rather than being dragged back.
   */
  entityQualityGuardGroupKey: string | null;
  clearEntityQualityGuardGroupKey: () => void;
  /**
   * True when the sidebar entity context is authoritative for the route currently shown:
   * the route is project-level, or it is an entity route whose identity matches the
   * context last set by that route's owner. Consumers use this to avoid acting on a
   * mid-navigation flip (e.g. clearing a deep-linked clarification thread). LAT-575.
   */
  isEntityContextSettledForRoute: boolean;
  setEntityContext: (
    entityType: string | null,
    entityId: string | null,
    entityName: string | null,
    options?: {
      tabKey?: string | null;
      fieldId?: string | null;
      qualityGuardGroupKey?: string | null;
    }
  ) => void;

  versionsSelectedId: string | null;
  setVersionsSelectedId: (id: string | null) => void;

  hasVersionController: boolean;
  isEntityDirty: boolean;
  setIsEntityDirty: (dirty: boolean) => void;
  versionsViewState: RightSidebarVersionsViewState;
  setVersionsViewState: (state: RightSidebarVersionsViewState) => void;
  clearVersionsViewState: () => void;

  versionActionIntent: RightSidebarVersionActionIntent | null;
  requestVersionPreview: (versionId: Id<"entityVersions">, versionNumber?: number) => void;
  requestExitVersionPreview: () => void;
  requestVersionCompare: (
    versionId: Id<"entityVersions">,
    versionNumber?: number,
    options?: VersionCompareOptions
  ) => void;
  requestVersionComparePair: (
    baselineVersionId: Id<"entityVersions"> | null,
    targetVersionId: Id<"entityVersions">,
    baselineVersionNumber?: number,
    targetVersionNumber?: number,
    options?: VersionCompareOptions
  ) => void;
  requestExitVersionCompare: () => void;
  requestCloseVersionPanel: () => void;
  requestDiscardDraft: () => void;
  setDiscardDraftCallback: (cb: (() => void) | null) => void;
  /**
   * Write the open element's pending draft edits out now, and wait for them to land.
   *
   * The auto-save is debounced, so at any moment the last second of typing may exist only in the
   * editor. Anything that is about to commit that draft on the user's behalf has to close that
   * gap first, or it commits a version that is missing what they just typed (LAT-845).
   */
  requestDraftFlush: () => Promise<void>;
  setDraftFlushCallback: (cb: (() => Promise<void>) | null) => void;
}

interface RightSidebarClarificationContextValue {
  clarificationFilter: ClarificationRequestFilter;
  setClarificationFilter: (filter: ClarificationRequestFilter) => void;
  clarificationViewMode: ClarificationViewMode;
  setClarificationViewMode: (mode: ClarificationViewMode) => void;
  selectedClarificationId: string | null;
  setSelectedClarificationId: (id: string | null) => void;
  isCreatingClarification: boolean;
  setIsCreatingClarification: (value: boolean) => void;
  clarificationCreateFieldName: string | null;
  linkedClarificationIds: string[];
  linkedPrimaryClarificationId: string | null;
  linkedClarificationActivationKey: number;
  setClarificationCreateFieldName: (fieldName: string | null) => void;
  clearLinkedClarifications: () => void;
  openCreateClarification: (fieldName?: string) => void;
  openClarificationList: (clarificationId?: string | null) => void;
  openClarificationThread: (clarificationId: string, options?: { replace?: boolean }) => void;
  openClarificationIndicatorMatches: (clarificationIds: string[]) => void;
}

interface RightSidebarVersionControllerOptions {
  enabled: boolean;
  currentViewedVersionId: Id<"entityVersions"> | null;
  diffTargetVersionId: Id<"entityVersions"> | null;
  latestSavedVersionId?: Id<"entityVersions"> | null;
  isDiffMode: boolean;
  isRouteVersionMode?: boolean;
  onPreviewVersion: (versionId: Id<"entityVersions">, versionNumber?: number) => void;
  onExitPreview: () => void;
  onCompareVersion: (
    versionId: Id<"entityVersions">,
    versionNumber?: number,
    options?: VersionCompareOptions
  ) => void;
  onComparePair: (
    baselineVersionId: Id<"entityVersions"> | null,
    targetVersionId: Id<"entityVersions">,
    baselineVersionNumber?: number,
    targetVersionNumber?: number,
    options?: VersionCompareOptions
  ) => void;
  onExitCompare: () => void;
  onCloseVersionPanel?: () => void;
}

const defaultVersionsViewState: RightSidebarVersionsViewState = {
  currentViewedVersionId: null,
  latestSavedVersionId: null,
  diffTargetVersionId: null,
  isDiffMode: false,
  isRouteVersionMode: false,
};

const defaultClarificationIndicatorLinkState: ClarificationIndicatorLinkState = {
  clarificationIds: [],
  primaryClarificationId: null,
  activationKey: 0,
};

const clarificationResetSearchParams: TabSearchParams = {
  crc: undefined,
  crcf: undefined,
  crv: undefined,
  crs: undefined,
};

const RightSidebarProjectContext = React.createContext<RightSidebarProjectContextValue | null>(
  null
);
const RightSidebarUiContext = React.createContext<RightSidebarUiContextValue | null>(null);
const RightSidebarEntityContext = React.createContext<RightSidebarEntityContextValue | null>(null);
const RightSidebarClarificationContext =
  React.createContext<RightSidebarClarificationContextValue | null>(null);
const RightSidebarNavigationContext =
  React.createContext<RightSidebarNavigationContextValue | null>(null);
const RightSidebarClaudiaContext = React.createContext<RightSidebarClaudiaContextValue | null>(
  null
);

function useRequiredContext<T>(context: React.Context<T | null>, hookName: string): T {
  const value = React.useContext(context);
  if (!value) {
    throw new Error(`${hookName} must be used within a RightSidebarProvider.`);
  }
  return value;
}

export function useRightSidebarProject(): RightSidebarProjectContextValue {
  return useRequiredContext(RightSidebarProjectContext, "useRightSidebarProject");
}

export function useRightSidebarUi(): RightSidebarUiContextValue {
  return useRequiredContext(RightSidebarUiContext, "useRightSidebarUi");
}

export function useRightSidebarEntity(): RightSidebarEntityContextValue {
  return useRequiredContext(RightSidebarEntityContext, "useRightSidebarEntity");
}

/**
 * The entity and UI slices when the caller happens to be inside the sidebar, and null when it is
 * not.
 *
 * The Claudia panel renders in two places — beside an open element in the right sidebar, and on
 * its own full-screen page, which has no provider above it. A component shared by both cannot use
 * the throwing accessors to reach affordances that only exist beside an editor; it has to be able
 * to ask, and get "no".
 */
export function useOptionalRightSidebarEntity(): RightSidebarEntityContextValue | null {
  return React.useContext(RightSidebarEntityContext);
}

export function useOptionalRightSidebarUi(): RightSidebarUiContextValue | null {
  return React.useContext(RightSidebarUiContext);
}

export function useRightSidebarClarification(): RightSidebarClarificationContextValue {
  return useRequiredContext(RightSidebarClarificationContext, "useRightSidebarClarification");
}

export function useRightSidebarNavigation(): RightSidebarNavigationContextValue {
  return useRequiredContext(RightSidebarNavigationContext, "useRightSidebarNavigation");
}

export function useRightSidebarClaudia(): RightSidebarClaudiaContextValue {
  return useRequiredContext(RightSidebarClaudiaContext, "useRightSidebarClaudia");
}

export function useRightSidebarVersionController({
  enabled,
  currentViewedVersionId,
  diffTargetVersionId,
  latestSavedVersionId = null,
  isDiffMode,
  isRouteVersionMode = false,
  onPreviewVersion,
  onExitPreview,
  onCompareVersion,
  onComparePair,
  onExitCompare,
  onCloseVersionPanel,
}: RightSidebarVersionControllerOptions): void {
  const { setVersionsViewState, clearVersionsViewState, versionActionIntent } =
    useRightSidebarEntity();
  const lastHandledRequestRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!enabled) {
      clearVersionsViewState();
      return;
    }

    setVersionsViewState({
      currentViewedVersionId,
      diffTargetVersionId,
      latestSavedVersionId,
      isDiffMode,
      isRouteVersionMode,
    });

    return () => {
      clearVersionsViewState();
    };
  }, [
    enabled,
    clearVersionsViewState,
    setVersionsViewState,
    currentViewedVersionId,
    diffTargetVersionId,
    latestSavedVersionId,
    isDiffMode,
    isRouteVersionMode,
  ]);

  React.useEffect(() => {
    if (!enabled || !versionActionIntent) {
      return;
    }

    if (lastHandledRequestRef.current === versionActionIntent.requestId) {
      return;
    }

    lastHandledRequestRef.current = versionActionIntent.requestId;

    switch (versionActionIntent.kind) {
      case "preview": {
        // Reselecting the baseline version while in compare mode should close compare
        // and return to plain preview mode for that version.
        if (isDiffMode && currentViewedVersionId === versionActionIntent.versionId) {
          onExitCompare();
          return;
        }
        onPreviewVersion(versionActionIntent.versionId, versionActionIntent.versionNumber);
        return;
      }
      case "exitPreview": {
        onExitPreview();
        return;
      }
      case "compare": {
        onCompareVersion(versionActionIntent.versionId, versionActionIntent.versionNumber, {
          routeBehavior: versionActionIntent.routeBehavior,
        });
        return;
      }
      case "comparePair": {
        onComparePair(
          versionActionIntent.baselineVersionId,
          versionActionIntent.targetVersionId,
          versionActionIntent.baselineVersionNumber,
          versionActionIntent.targetVersionNumber,
          { routeBehavior: versionActionIntent.routeBehavior }
        );
        return;
      }
      case "exitCompare": {
        onExitCompare();
        return;
      }
      case "closePanel": {
        onCloseVersionPanel?.();
        return;
      }
      default: {
        return;
      }
    }
  }, [
    enabled,
    versionActionIntent,
    onPreviewVersion,
    onExitPreview,
    onCompareVersion,
    onComparePair,
    onExitCompare,
    onCloseVersionPanel,
    isDiffMode,
    currentViewedVersionId,
  ]);
}

export function RightSidebarProvider({
  children,
  projectId,
  searchParams,
  memory,
  tabId,
  updateTabSearchParams,
  updateTabMemory,
}: RightSidebarProviderProps) {
  const isMobile = useIsMobile();
  const navigate = useProjectNavigate();
  const { currentPath, parsed } = useProjectRoute();
  const [openMobile, setOpenMobile] = React.useState(false);

  const [entityType, setEntityType] = React.useState<string | null>(null);
  const [entityId, setEntityId] = React.useState<string | null>(null);
  const [entityName, setEntityName] = React.useState<string | null>(null);
  const [entityTabKey, setEntityTabKey] = React.useState<string | null>(null);
  const [entityFieldId, setEntityFieldId] = React.useState<string | null>(null);
  const [entityQualityGuardGroupKey, setEntityQualityGuardGroupKey] = React.useState<string | null>(
    null
  );

  // Route-identity token for the current entity route, stable across tab/version
  // suffixes (LAT-575). Used to decide whether the sidebar entity context belongs to the
  // route now being shown — a deep-link clarification thread must not be cleared until the
  // entity context has settled for the destination route, not a mid-navigation flip.
  const routeIsEntity = parsed.routeKey === "entity";
  const currentRouteEntityToken = routeIsEntity
    ? (parsed.baseEntityPath ?? parsed.canonicalPath)
    : null;
  const currentRouteEntityTokenRef = React.useRef(currentRouteEntityToken);
  currentRouteEntityTokenRef.current = currentRouteEntityToken;
  // The route token captured the last time the entity owner set context. When it matches
  // the current route token, the sidebar entity context is authoritative for this route.
  const [entityContextRouteToken, setEntityContextRouteToken] = React.useState<string | null>(null);
  const isEntityContextSettledForRoute =
    !routeIsEntity ||
    (entityContextRouteToken !== null && entityContextRouteToken === currentRouteEntityToken);
  const [claudiaViewContext, setClaudiaViewContextInternal] =
    React.useState<RightSidebarClaudiaViewContext | null>(null);
  const [pendingAskClaudiaIntent, setPendingAskClaudiaIntent] =
    React.useState<RightSidebarClaudiaAskIntent | null>(null);
  const askClaudiaRequestCounterRef = React.useRef(0);

  const [hasVersionController, setHasVersionController] = React.useState(false);
  const [isEntityDirty, setIsEntityDirty] = React.useState(false);
  const discardDraftCallbackRef = React.useRef<(() => void) | null>(null);
  const draftFlushCallbackRef = React.useRef<(() => Promise<void>) | null>(null);
  const [versionsViewState, setVersionsViewStateInternal] =
    React.useState<RightSidebarVersionsViewState>(defaultVersionsViewState);
  const [versionActionIntent, setVersionActionIntent] =
    React.useState<RightSidebarVersionActionIntent | null>(null);
  const versionActionCounterRef = React.useRef(0);

  const open = !((searchParams.rc as boolean) ?? true);
  const activePanel: RightSidebarTab =
    tabMap[(searchParams.rt as "v" | "g" | "cr" | "t" | "cl") ?? "v"];
  const isSidebarOpen = isMobile ? openMobile : open;
  const trackedPanelStateRef = React.useRef<{
    panel: RightSidebarTab;
    open: boolean;
  }>({ panel: activePanel, open: false });

  React.useEffect(() => {
    const previous = trackedPanelStateRef.current;
    if (previous.open && (!isSidebarOpen || previous.panel !== activePanel)) {
      trackNavigationEvent("panel_closed", {
        panel: previous.panel,
        project_id: projectId,
      });
    }

    if (isSidebarOpen && (!previous.open || previous.panel !== activePanel)) {
      trackNavigationEvent("panel_opened", {
        panel: activePanel,
        surface: "right_panel",
        project_id: projectId,
      });
    }

    trackedPanelStateRef.current = { panel: activePanel, open: isSidebarOpen };
  }, [activePanel, isSidebarOpen, projectId]);
  const versionsSelectedId = (searchParams.vs as string) ?? null;
  const clarificationFilter: ClarificationRequestFilter =
    filterMap[(searchParams.crf as "o" | "a" | "c") ?? "o"];
  const clarificationViewMode: ClarificationViewMode =
    clarificationViewModeMap[(searchParams.crv as "l" | "t") ?? "l"];
  const selectedClarificationId = (searchParams.crs as string) ?? null;
  const isCreatingClarification = (searchParams.crc as boolean) ?? false;
  const clarificationCreateFieldName = (searchParams.crcf as string) ?? null;
  const [clarificationIndicatorLinkState, setClarificationIndicatorLinkState] =
    React.useState<ClarificationIndicatorLinkState>(defaultClarificationIndicatorLinkState);

  const updateSearch = React.useCallback(
    (updates: Partial<TabSearchParams>, options?: { replace?: boolean }) => {
      updateTabSearchParams(tabId, updates, options);
    },
    [tabId, updateTabSearchParams]
  );

  const setOpen = React.useCallback(
    (value: boolean) => {
      updateSearch({ rc: value ? false : undefined });
    },
    [updateSearch]
  );

  const clearLinkedClarifications = React.useCallback(() => {
    setClarificationIndicatorLinkState((current) => {
      if (current.clarificationIds.length === 0 && current.primaryClarificationId === null) {
        return current;
      }
      return {
        ...current,
        clarificationIds: [],
        primaryClarificationId: null,
      };
    });
  }, []);

  const clearLinkedClarificationsForActivation = React.useCallback((activationKey: number) => {
    setClarificationIndicatorLinkState((current) => {
      if (current.activationKey !== activationKey) {
        return current;
      }
      if (current.clarificationIds.length === 0 && current.primaryClarificationId === null) {
        return current;
      }
      return {
        ...current,
        clarificationIds: [],
        primaryClarificationId: null,
      };
    });
  }, []);

  const setActivePanel = React.useCallback(
    (panel: RightSidebarTab) => {
      if (panel !== "cr") {
        clearLinkedClarifications();
      }
      updateSearch({
        rt: panel === "versions" ? undefined : tabMapReverse[panel],
        ...(panel === "cr" ? {} : clarificationResetSearchParams),
      });
    },
    [clearLinkedClarifications, updateSearch]
  );

  const expandToPanel = React.useCallback(
    (panel: RightSidebarTab, options?: { replace?: boolean }) => {
      if (panel !== "cr") {
        clearLinkedClarifications();
      }
      updateSearch(
        {
          rt: panel === "versions" ? undefined : tabMapReverse[panel],
          rc: false,
          ...(panel === "cr" ? {} : clarificationResetSearchParams),
        },
        options
      );
    },
    [clearLinkedClarifications, updateSearch]
  );

  const clearVersionsViewState = React.useCallback(() => {
    setHasVersionController(false);
    setIsEntityDirty(false);
    setVersionsViewStateInternal(defaultVersionsViewState);
    setVersionActionIntent(null);
  }, []);

  const setVersionsViewState = React.useCallback((state: RightSidebarVersionsViewState) => {
    setHasVersionController(true);
    setVersionsViewStateInternal(state);
  }, []);

  const requestVersionAction = React.useCallback((action: RightSidebarVersionAction) => {
    versionActionCounterRef.current += 1;
    setVersionActionIntent({
      ...action,
      requestId: versionActionCounterRef.current,
    });
  }, []);

  const shouldCloseVersionsPanelSemantically = React.useCallback(
    () =>
      activePanel === "versions" &&
      hasVersionController &&
      (versionsViewState.isRouteVersionMode ||
        versionsViewState.isDiffMode ||
        versionsViewState.currentViewedVersionId !== null ||
        versionsViewState.diffTargetVersionId !== null),
    [activePanel, hasVersionController, versionsViewState]
  );

  const closeVersionsPanelOrSidebar = React.useCallback(() => {
    if (shouldCloseVersionsPanelSemantically()) {
      requestVersionAction({ kind: "closePanel" });
      return;
    }

    updateSearch({ rc: undefined });
  }, [requestVersionAction, shouldCloseVersionsPanelSemantically, updateSearch]);

  const togglePanel = React.useCallback(
    (panel: RightSidebarTab) => {
      if (isMobile) {
        if (openMobile && activePanel === panel) {
          if (panel === "cr") {
            clearLinkedClarifications();
          }
          if (panel === "versions" && shouldCloseVersionsPanelSemantically()) {
            requestVersionAction({ kind: "closePanel" });
          }
          setOpenMobile(false);
        } else {
          setOpenMobile(true);
          setActivePanel(panel);
        }
      } else {
        if (open && activePanel === panel) {
          if (panel === "cr") {
            clearLinkedClarifications();
          }
          if (panel === "versions") {
            closeVersionsPanelOrSidebar();
          } else {
            updateSearch({
              rc: undefined,
              ...(panel === "cr" ? clarificationResetSearchParams : {}),
            });
          }
        } else {
          expandToPanel(panel);
        }
      }
    },
    [
      activePanel,
      clearLinkedClarifications,
      expandToPanel,
      isMobile,
      open,
      openMobile,
      requestVersionAction,
      setActivePanel,
      closeVersionsPanelOrSidebar,
      shouldCloseVersionsPanelSemantically,
      updateSearch,
    ]
  );

  const toggleSidebar = React.useCallback(() => {
    if (isMobile) {
      if (openMobile) {
        if (activePanel === "cr") {
          clearLinkedClarifications();
          updateSearch(clarificationResetSearchParams);
        }
        if (activePanel === "versions" && shouldCloseVersionsPanelSemantically()) {
          requestVersionAction({ kind: "closePanel" });
        }
        setOpenMobile(false);
        return;
      }

      setOpenMobile(true);
      return;
    }

    if (open && activePanel === "cr") {
      clearLinkedClarifications();
      updateSearch({
        rc: undefined,
        ...clarificationResetSearchParams,
      });
      return;
    }

    if (open && activePanel === "versions") {
      closeVersionsPanelOrSidebar();
      return;
    }

    setOpen(!open);
  }, [
    activePanel,
    clearLinkedClarifications,
    closeVersionsPanelOrSidebar,
    isMobile,
    open,
    openMobile,
    requestVersionAction,
    setOpen,
    shouldCloseVersionsPanelSemantically,
    updateSearch,
  ]);

  const setEntityContext = React.useCallback(
    (
      type: string | null,
      id: string | null,
      name: string | null,
      options?: {
        tabKey?: string | null;
        fieldId?: string | null;
        qualityGuardGroupKey?: string | null;
      }
    ) => {
      setEntityType(type);
      setEntityId(id);
      setEntityName(name);
      setEntityTabKey(options?.tabKey ?? null);
      setEntityFieldId(options?.fieldId ?? null);
      setEntityQualityGuardGroupKey(options?.qualityGuardGroupKey ?? null);
      // Stamp the route this context belongs to (read from a ref to avoid a stale
      // closure). Cleared context detaches from any route.
      setEntityContextRouteToken(type && id ? currentRouteEntityTokenRef.current : null);

      if (!type || !id) {
        clearVersionsViewState();
      }
    },
    [clearVersionsViewState]
  );

  const clearEntityQualityGuardGroupKey = React.useCallback(() => {
    setEntityQualityGuardGroupKey(null);
  }, []);

  const setClaudiaViewContext = React.useCallback(
    (context: RightSidebarClaudiaViewContext | null) => {
      setClaudiaViewContextInternal((current) =>
        areClaudiaViewContextsEqual(current, context) ? current : context
      );
    },
    []
  );

  const setAskClaudiaIntent = React.useCallback(
    (intent: Omit<RightSidebarClaudiaAskIntent, "requestKey">) => {
      askClaudiaRequestCounterRef.current += 1;
      setPendingAskClaudiaIntent({
        ...intent,
        requestKey: askClaudiaRequestCounterRef.current,
      });
    },
    []
  );

  const clearAskClaudiaIntent = React.useCallback(() => {
    setPendingAskClaudiaIntent(null);
  }, []);

  const setVersionsSelectedId = React.useCallback(
    (id: string | null) => {
      updateSearch({ vs: id ?? undefined });
    },
    [updateSearch]
  );

  const setClarificationFilter = React.useCallback(
    (filter: ClarificationRequestFilter) => {
      updateSearch({ crf: filter === "open" ? undefined : filterMapReverse[filter] });
    },
    [updateSearch]
  );

  const setClarificationViewMode = React.useCallback(
    (mode: ClarificationViewMode) => {
      updateSearch({ crv: mode === "list" ? undefined : clarificationViewModeMapReverse[mode] });
    },
    [updateSearch]
  );

  const setSelectedClarificationId = React.useCallback(
    (id: string | null) => {
      updateSearch({ crs: id ?? undefined });
    },
    [updateSearch]
  );

  const setIsCreatingClarification = React.useCallback(
    (value: boolean) => {
      updateSearch({ crc: value || undefined });
    },
    [updateSearch]
  );

  const setClarificationCreateFieldName = React.useCallback(
    (fieldName: string | null) => {
      updateSearch({ crcf: fieldName ?? undefined });
    },
    [updateSearch]
  );

  const openCreateClarification = React.useCallback(
    (fieldName?: string) => {
      clearLinkedClarifications();
      updateSearch({
        crc: true,
        crv: undefined,
        crcf: fieldName ?? undefined,
        rt: tabMapReverse.cr,
        rc: false,
        crs: undefined,
      });
    },
    [clearLinkedClarifications, updateSearch]
  );

  const openClarificationList = React.useCallback(
    (clarificationId?: string | null) => {
      clearLinkedClarifications();
      updateSearch({
        crc: undefined,
        crcf: undefined,
        crv: undefined,
        rt: tabMapReverse.cr,
        rc: false,
        crs: clarificationId ?? undefined,
      });
    },
    [clearLinkedClarifications, updateSearch]
  );

  const openClarificationThread = React.useCallback(
    (clarificationId: string, options?: { replace?: boolean }) => {
      clearLinkedClarifications();
      updateSearch(
        {
          crc: undefined,
          crcf: undefined,
          crv: clarificationViewModeMapReverse.thread,
          rt: tabMapReverse.cr,
          rc: false,
          crs: clarificationId,
        },
        options
      );
    },
    [clearLinkedClarifications, updateSearch]
  );

  const openClarificationIndicatorMatches = React.useCallback(
    (clarificationIds: string[]) => {
      const uniqueClarificationIds = Array.from(
        new Set(
          clarificationIds.filter(
            (clarificationId) => typeof clarificationId === "string" && clarificationId
          )
        )
      );
      if (uniqueClarificationIds.length === 0) {
        openClarificationList();
        return;
      }

      if (uniqueClarificationIds.length === 1) {
        openClarificationThread(uniqueClarificationIds[0] as string);
        return;
      }

      setClarificationIndicatorLinkState((current) => ({
        clarificationIds: uniqueClarificationIds,
        primaryClarificationId: uniqueClarificationIds[0] ?? null,
        activationKey: current.activationKey + 1,
      }));

      updateSearch({
        crc: undefined,
        crcf: undefined,
        crv: undefined,
        rt: tabMapReverse.cr,
        rc: false,
        crs: undefined,
        crf: clarificationFilter === "all" ? filterMapReverse.all : undefined,
      });
    },
    [openClarificationList, openClarificationThread, clarificationFilter, updateSearch]
  );

  React.useEffect(() => {
    if (clarificationIndicatorLinkState.clarificationIds.length === 0) {
      return;
    }

    const activationKey = clarificationIndicatorLinkState.activationKey;
    const timeoutId = window.setTimeout(() => {
      clearLinkedClarificationsForActivation(activationKey);
    }, 10_000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    clarificationIndicatorLinkState.activationKey,
    clarificationIndicatorLinkState.clarificationIds.length,
    clearLinkedClarificationsForActivation,
  ]);

  React.useEffect(() => {
    if (activePanel !== "cr") {
      clearLinkedClarifications();
    }
  }, [activePanel, clearLinkedClarifications]);

  React.useEffect(() => {
    if (!isCreatingClarification && clarificationViewMode !== "thread") {
      return;
    }
    clearLinkedClarifications();
  }, [clearLinkedClarifications, isCreatingClarification, clarificationViewMode]);

  const state: "expanded" | "collapsed" = open ? "expanded" : "collapsed";

  const projectValue = React.useMemo<RightSidebarProjectContextValue>(
    () => ({
      projectId,
      tabId,
    }),
    [projectId, tabId]
  );

  const uiValue = React.useMemo<RightSidebarUiContextValue>(
    () => ({
      state,
      open,
      setOpen,
      openMobile,
      setOpenMobile,
      isMobile,
      toggleSidebar,
      activePanel,
      setActivePanel,
      expandToPanel,
      togglePanel,
    }),
    [
      state,
      open,
      setOpen,
      openMobile,
      isMobile,
      toggleSidebar,
      activePanel,
      setActivePanel,
      expandToPanel,
      togglePanel,
    ]
  );

  const entityValue = React.useMemo<RightSidebarEntityContextValue>(
    () => ({
      entityType,
      entityId,
      entityName,
      entityTabKey,
      entityFieldId,
      entityQualityGuardGroupKey,
      clearEntityQualityGuardGroupKey,
      isEntityContextSettledForRoute,
      setEntityContext,
      versionsSelectedId,
      setVersionsSelectedId,
      hasVersionController,
      isEntityDirty,
      setIsEntityDirty,
      versionsViewState,
      setVersionsViewState,
      clearVersionsViewState,
      versionActionIntent,
      requestVersionPreview: (versionId, versionNumber) =>
        requestVersionAction({ kind: "preview", versionId, versionNumber }),
      requestExitVersionPreview: () => requestVersionAction({ kind: "exitPreview" }),
      requestVersionCompare: (versionId, versionNumber, options) =>
        requestVersionAction({
          kind: "compare",
          versionId,
          versionNumber,
          routeBehavior: options?.routeBehavior,
        }),
      requestVersionComparePair: (
        baselineVersionId,
        targetVersionId,
        baselineVersionNumber,
        targetVersionNumber,
        options
      ) =>
        requestVersionAction({
          kind: "comparePair",
          baselineVersionId,
          targetVersionId,
          baselineVersionNumber,
          targetVersionNumber,
          routeBehavior: options?.routeBehavior,
        }),
      requestExitVersionCompare: () => requestVersionAction({ kind: "exitCompare" }),
      requestCloseVersionPanel: () => requestVersionAction({ kind: "closePanel" }),
      requestDiscardDraft: () => discardDraftCallbackRef.current?.(),
      setDiscardDraftCallback: (cb: (() => void) | null) => {
        discardDraftCallbackRef.current = cb;
      },
      requestDraftFlush: async () => {
        await draftFlushCallbackRef.current?.();
      },
      setDraftFlushCallback: (cb: (() => Promise<void>) | null) => {
        draftFlushCallbackRef.current = cb;
      },
    }),
    [
      entityType,
      entityId,
      entityName,
      entityTabKey,
      entityFieldId,
      entityQualityGuardGroupKey,
      clearEntityQualityGuardGroupKey,
      isEntityContextSettledForRoute,
      setEntityContext,
      versionsSelectedId,
      setVersionsSelectedId,
      hasVersionController,
      isEntityDirty,
      versionsViewState,
      setVersionsViewState,
      clearVersionsViewState,
      versionActionIntent,
      requestVersionAction,
    ]
  );

  const clarificationValue = React.useMemo<RightSidebarClarificationContextValue>(
    () => ({
      clarificationFilter,
      setClarificationFilter,
      clarificationViewMode,
      setClarificationViewMode,
      selectedClarificationId,
      setSelectedClarificationId,
      isCreatingClarification,
      setIsCreatingClarification,
      clarificationCreateFieldName,
      linkedClarificationIds: clarificationIndicatorLinkState.clarificationIds,
      linkedPrimaryClarificationId: clarificationIndicatorLinkState.primaryClarificationId,
      linkedClarificationActivationKey: clarificationIndicatorLinkState.activationKey,
      setClarificationCreateFieldName,
      clearLinkedClarifications,
      openCreateClarification,
      openClarificationList,
      openClarificationThread,
      openClarificationIndicatorMatches,
    }),
    [
      clarificationFilter,
      setClarificationFilter,
      clarificationViewMode,
      setClarificationViewMode,
      selectedClarificationId,
      setSelectedClarificationId,
      isCreatingClarification,
      setIsCreatingClarification,
      clarificationCreateFieldName,
      clarificationIndicatorLinkState.activationKey,
      clarificationIndicatorLinkState.primaryClarificationId,
      clarificationIndicatorLinkState.clarificationIds,
      setClarificationCreateFieldName,
      clearLinkedClarifications,
      openCreateClarification,
      openClarificationList,
      openClarificationThread,
      openClarificationIndicatorMatches,
    ]
  );

  const claudiaValue = React.useMemo<RightSidebarClaudiaContextValue>(
    () => ({
      currentViewContext: claudiaViewContext,
      setClaudiaViewContext,
      pendingAskClaudiaIntent,
      setAskClaudiaIntent,
      clearAskClaudiaIntent,
    }),
    [
      claudiaViewContext,
      setClaudiaViewContext,
      pendingAskClaudiaIntent,
      setAskClaudiaIntent,
      clearAskClaudiaIntent,
    ]
  );

  const rememberedAssistantThreadId = React.useMemo(
    () => parseAssistantThreadIdFromPath(memory.assistantPath ?? ""),
    [memory.assistantPath]
  );

  const setRememberedAssistantThread = React.useCallback(
    (threadId: string) => {
      // Panel thread changes do not navigate, so they write assistant memory directly.
      // Route-driven assistant memory is still owned by the project navigation policy.
      updateTabMemory(tabId, { assistantPath: buildAssistantThreadPath(threadId) });
    },
    [tabId, updateTabMemory]
  );
  const clearRememberedAssistantThread = React.useCallback(() => {
    updateTabMemory(tabId, { assistantPath: undefined });
  }, [tabId, updateTabMemory]);

  const navigationValue = React.useMemo<RightSidebarNavigationContextValue>(
    () => ({
      navigate,
      currentPath,
      rememberedAssistantThreadId,
      setRememberedAssistantThread,
      clearRememberedAssistantThread,
    }),
    [
      navigate,
      currentPath,
      rememberedAssistantThreadId,
      setRememberedAssistantThread,
      clearRememberedAssistantThread,
    ]
  );

  return (
    <RightSidebarProjectContext.Provider value={projectValue}>
      <RightSidebarUiContext.Provider value={uiValue}>
        <RightSidebarEntityContext.Provider value={entityValue}>
          <RightSidebarClarificationContext.Provider value={clarificationValue}>
            <RightSidebarClaudiaContext.Provider value={claudiaValue}>
              <RightSidebarNavigationContext.Provider value={navigationValue}>
                {children}
              </RightSidebarNavigationContext.Provider>
            </RightSidebarClaudiaContext.Provider>
          </RightSidebarClarificationContext.Provider>
        </RightSidebarEntityContext.Provider>
      </RightSidebarUiContext.Provider>
    </RightSidebarProjectContext.Provider>
  );
}
